import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Grid } from '@material-ui/core';
import moment from 'moment';
import { DateRange } from '@material-ui/pickers';
import { useApiError, useAppData, useLoading } from '../../../providers';
import { ReportTable, ReportTitle, ReportToolbarWithFilters } from './components';
import { CashbackCommissionReport } from './IBClientsCashbackModel';
import { trackAmplitudeData } from '../../../services/amplitudeService';
import { getCashbackReport } from './IBClientsCashbackService';
import { useCommonStyles } from '../../../styles';
import { Header, PageTitle } from '../../../components';
import { useStyles } from './styles';
import { ColumnConfig, formatNumber, formatDate } from '../DataTable';

export const IBClientsCashback: FC = () => {
  const classes = useStyles();
  const commonClasses = useCommonStyles();
  const { setIsLoading } = useLoading();
  const { addError } = useApiError();
  const { translations } = useAppData();
  const [selectedPartnerships, setSelectedPartnerships] = useState<string[]>(['All']);
  const [selectedDateRange, setSelectedDateRange] = React.useState<DateRange<Date>>([moment().subtract(1, 'months').toDate(), moment().subtract(1, 'days').toDate()]);
  const [showReport, setShowReport] = useState<boolean>(false);
  const [reportData, setReportData] = useState<CashbackCommissionReport[]>([]);
  const [translation, setTranslation] = useState<any>([]);
  const [selectedColumns, setSelectedColumns] = useState<string[]>([]);

  useEffect(() => {
    setTranslation(JSON.parse(translations));
  }, [translations]);

  const columns = useMemo(
    (): ColumnConfig<CashbackCommissionReport>[] => [
      { key: 'calculationDate', label: translation.calculation_date || '', type: 'date', format: (v) => formatDate(v, 'DD-MM-YYYY') },
      {
        key: 'periodStart',
        label: translation.calculation_period || '',
        type: 'date',
        format: (v, row) => (v || row.periodEnd ? `${moment(row.periodStart).format('DD-MM-YYYY')} - ${moment(row.periodEnd).format('DD-MM-YYYY')}` : '-'),
      },
      { key: 'campaignName', label: translation.campaign_name || '', type: 'string' },
      { key: 'partnershipCodes', label: translation.text_1251 || '', type: 'string', format: (v) => (v && v.length > 0 ? v.join(',') : '-'), sortable: false },
      { key: 'volumeUsd', label: translation.volume_usd || '', type: 'number', format: (v) => formatNumber(v), summable: true },
      { key: 'volumeLots', label: translation.volume_lots || '', type: 'number', format: (v) => formatNumber(v), summable: true },
      { key: 'commission', label: translation.commission_usd || '', type: 'number', format: (v) => formatNumber(v), summable: true },
      { key: 'paidCashback', label: translation.cashback_paid || '', type: 'number', format: (v) => formatNumber(v), summable: true },
      { key: 'pendingCashback', label: translation.cashback_pending || '', type: 'number', format: (v) => formatNumber(v), summable: true },
      { key: 'rejectedCashback', label: translation.cashback_rejected || '', type: 'number', format: (v) => formatNumber(v), summable: true },
      { key: 'failedCashback', label: translation.cashback_failed || '', type: 'number', format: (v) => formatNumber(v), summable: true },
    ],
    [translation],
  );

  const changeSelectedPartnerships = useCallback(async (options: any) => {
    setSelectedPartnerships(options);
    setShowReport(false);
    setReportData([]);
  }, []);

  const getReportData = useCallback(
    async (partnerships: string[], dateRange: DateRange<Date>) => {
      setIsLoading(true);
      try {
        trackAmplitudeData('partnerPortal_action_report', {
          type: 'Requested',
          report_name: 'IB Clients Cashback',
        });

        const response = await getCashbackReport(moment(dateRange[0]).format('YYYY-MM-DD'), moment(dateRange[1]).format('YYYY-MM-DD'), partnerships.includes('All') ? [] : partnerships);
        setReportData(response.data);
        setShowReport(true);
      } catch (error) {
        addError(JSON.stringify(error.response));
      } finally {
        setIsLoading(false);
      }
    },
    [addError, setIsLoading],
  );

  const handleNewReportRequest = useCallback(async () => {
    await getReportData(selectedPartnerships, selectedDateRange);
  }, [selectedPartnerships, selectedDateRange, getReportData]);

  const handleFiltersApplied = useCallback(
    async (partnerships: any) => {
      setSelectedPartnerships(partnerships);
      await getReportData(partnerships, selectedDateRange);
    },
    [selectedDateRange, getReportData],
  );

  return (
    <>
      <Header title={translation.text_6663} />
      <Grid container direction="column" className={classes.root}>
        <PageTitle title={translation.text_6663} />
        <Grid container direction="column" className={commonClasses.mainContainer}>
          <ReportTitle
            translation={translation}
            title={translation.ib_clients_cashback}
            exportFileName={translation.ib_clients_cashback}
            columns={columns}
            allData={reportData}
            selectedColumns={selectedColumns}
            enableButtons={showReport}
            reportNameInEn="IB Clients Cashback"
          />
          <Grid container direction="column">
            <ReportToolbarWithFilters
              translation={translation}
              dateRange={selectedDateRange}
              handleDateRangeChange={(dateRange) => {
                setSelectedDateRange(dateRange);
                setShowReport(false);
              }}
              handlePartnershipsChange={changeSelectedPartnerships}
              handleButtonClick={handleNewReportRequest}
              handleFilterApplied={handleFiltersApplied}
            />
            {selectedDateRange[0] && (
              <Grid item xs={12} className={classes.margin}>
                <ReportTable dateFrom={selectedDateRange[0]} columns={columns} rows={reportData} translation={translation} selectedColumns={selectedColumns} onSelectColumns={setSelectedColumns} />
              </Grid>
            )}
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};
